'use client'

import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import Header from '@/components/Header'
import HeroParallax from '@/components/HeroParallax'
import Footer from '@/components/Footer'
import { getExperienceById, experiences } from '@/lib/data'
import styles from '@/styles/experience-page.module.css'
import { BreadcrumbSchema } from '@/lib/schema'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import '@/components/Adventures.css'

export default function MountainExtrasPage() {
  const thisExperience = getExperienceById('mountain-extras')
  const t = useTranslations('experiences')
  const tCommon = useTranslations('common')
  const params = useParams()
  const locale = params.locale as string

  const relatedExperiences = experiences.filter(e => e.id !== 'mountain-extras').slice(0, 2)

  const mainRef = useScrollAnimation<HTMLElement>({
    threshold: 0.12,
    rootMargin: '0px 0px -60px 0px',
  })

  if (!thisExperience) {
    return <div>{t('common.experienceNotFound')}</div>
  }

  return (
    <>
      <BreadcrumbSchema items={[
        { name: tCommon('home'), href: `/${locale}/` },
        { name: t('index.title'), href: `/${locale}/experiences/` },
        { name: thisExperience.title, href: `/${locale}/experiences/mountain-extras/` },
      ]} />
      <Header />

      <HeroParallax
        backgroundImage={thisExperience.heroImage}
        title={thisExperience.title}
        subtitle={thisExperience.subtitle}
        location={thisExperience.location}
      />

      <main className={styles.pageWrapper} ref={mainRef}>
        {/* Intro Section */}
        <section className={styles.experienceDetails}>
          <div className="container">
            <div className={styles.introSection}>
              <h2 className={styles.introTitle} data-scroll-item>{t('mountainExtras.introTitle')}</h2>
              <p className={styles.introDescription} data-scroll-item style={{ transitionDelay: '120ms' }}>{t('mountainExtras.introDescription')}</p>
            </div>
          </div>
        </section>


        {/* Gallery Section */}
        <section className={styles.gallery}>
          <div className="container">
            <div className={styles.sectionHeader} data-scroll-item>
              <h2 className={styles.sectionTitle}>{t('mountainExtras.galleryTitle')}</h2>
              <p className={styles.sectionDescription}>
                {t('mountainExtras.galleryDescription')}
              </p>
            </div>

            <div className={styles.galleryGrid}>
              {[
                { src: '/images/experiences/extras/extras-gallery-01.webp', alt: t('mountainExtras.alts.gallery01') },
                { src: '/images/experiences/extras/extras-gallery-02.webp', alt: t('mountainExtras.alts.gallery02') },
                { src: '/images/experiences/extras/extras-gallery-03.webp', alt: t('mountainExtras.alts.gallery03') },
                { src: '/images/experiences/extras/extras-gallery-04.webp', alt: t('mountainExtras.alts.gallery04') },
                { src: '/images/experiences/extras/extras-gallery-05.webp', alt: t('mountainExtras.alts.gallery05') },
                { src: '/images/experiences/extras/extras-gallery-06.webp', alt: t('mountainExtras.alts.gallery06') },
              ].map((image, idx) => (
                <div key={idx} className={styles.galleryItem} data-scroll-item style={{ transitionDelay: `${idx * 80}ms` }}>
                  <img src={image.src} alt={image.alt} loading="lazy" />
                </div>
              ))}
            </div>

            <div className={styles.sectionCta} data-scroll-item>
              <a href={`/${locale}/experiences/`} className="btn-primary">
                {t('common.exploreExperiences')} &rarr;
              </a>
            </div>
          </div>
        </section>

        {/* Related Experiences Section */}
        <section className={styles.relatedSection}>
          <div className="container">
            <h2 data-scroll-item>{t('common.relatedExperiences')}</h2>
            <div className={styles.relatedGrid}>
              {relatedExperiences.map((experience, i) => (
                <article key={experience.id} className={styles.relatedCard} data-scroll-item style={{ transitionDelay: `${120 + i * 120}ms` }}>
                  <div className={styles.relatedImage}>
                    <img src={experience.heroImage} alt={experience.title} loading="lazy" />
                  </div>
                  <div className={styles.relatedOverlay}>
                    <h3 className={styles.relatedTitle}>{experience.title}</h3>
                    <div className={styles.relatedContent}>
                      <p>{experience.subtitle}</p>
                      <a href={`/${locale}/experiences/${experience.id}/`} className={styles.relatedLink}>
                        {t('common.explore')} &rarr;
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className={styles.ctaSection}>
          <div className="container">
            <h2 data-scroll-item>{t('mountainExtras.ctaTitle')}</h2>
            <p data-scroll-item style={{ transitionDelay: '120ms' }}>{t('mountainExtras.ctaDescription')}</p>
            <a href={`/${locale}/contact/`} className={styles.ctaButton} data-scroll-item style={{ transitionDelay: '240ms' }}>
              {t('common.planYourExperience')}
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </>
  )
}
